import { createTheme, type Theme, type ThemeOptions } from "@mui/material/styles";
import { palettes } from "./palette";
import { getThemeSpec, type AppThemeId } from "./themeSpecs";

export const createAppTheme = (themeId: AppThemeId): Theme => {
  const spec = getThemeSpec(themeId);
  const mode = spec.mode;
  const source = palettes[spec.id];
  const { surface, colors } = source;

  const darkGreyRamp = {
    50: surface.sunken,
    100: surface.muted,
    200: surface.border,
    300: surface.borderStrong,
    400: surface.textDisabled,
    500: surface.textTertiary,
    600: surface.textSecondary,
    700: "#BAC6D4",
    800: "#D5DEE8",
    900: surface.text,
    A100: surface.muted,
    A200: surface.border,
    A400: surface.textTertiary,
    A700: surface.textSecondary,
  };

  const options: ThemeOptions = {
    palette: {
      mode,
      ...(mode === "dark" ? { grey: darkGreyRamp } : {}),
      primary: {
        light: colors.orange[300],
        main: colors.orange.base,
        dark: colors.orange[600],
        contrastText: "#FFFFFF",
      },
      secondary: {
        light: colors.blue[300],
        main: colors.blue.base,
        dark: colors.blue[700],
        contrastText: "#FFFFFF",
      },
      error: { light: colors.danger[300], main: colors.danger.base, dark: colors.danger[700], contrastText: "#FFFFFF" },
      warning: { light: colors.warning[300], main: colors.warning.base, dark: colors.warning[700], contrastText: "#FFFFFF" },
      info: { light: colors.info[300], main: colors.info.base, dark: colors.info[700], contrastText: "#FFFFFF" },
      success: { light: colors.success[300], main: colors.success.base, dark: colors.success[700], contrastText: "#FFFFFF" },
      background: { default: surface.page, paper: surface.base },
      text: { primary: surface.text, secondary: surface.textSecondary, disabled: surface.textDisabled },
      divider: surface.border,
      action: {
        hover: surface.hover,
        selected: surface.selected,
        disabled: surface.textDisabled,
        disabledBackground: surface.muted,
        focus: surface.hover,
      },
    },
    shape: { borderRadius: 8 },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            backgroundColor: surface.page,
            color: surface.text,
            transition: "background-color 180ms ease, color 180ms ease",
          },
          "::selection": { backgroundColor: surface.selected },
          "*::-webkit-scrollbar": { width: 10, height: 10 },
          "*::-webkit-scrollbar-track": { background: "transparent" },
          "*::-webkit-scrollbar-thumb": {
            backgroundColor: surface.borderStrong,
            borderRadius: 8,
            border: `2px solid ${surface.page}`,
          },
          "*::-webkit-scrollbar-thumb:hover": { backgroundColor: surface.textTertiary },
          "*": { scrollbarColor: `${surface.borderStrong} transparent` },
          "input:-webkit-autofill, input:-webkit-autofill:focus": {
            WebkitTextFillColor: surface.text,
            WebkitBoxShadow: `0 0 0 1000px ${surface.base} inset`,
            caretColor: surface.text,
          },
          img: { colorScheme: "normal" },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: { backgroundColor: surface.base, backgroundImage: "none", color: surface.text },
          outlined: { borderColor: surface.border },
        },
      },
      MuiCard: { styleOverrides: { root: { backgroundColor: surface.base, backgroundImage: "none" } } },
      MuiAppBar: {
        styleOverrides: {
          root: { backgroundColor: surface.base, color: surface.text, backgroundImage: "none" },
          colorPrimary: { backgroundColor: surface.base, color: surface.text },
        },
      },
      MuiDrawer: { styleOverrides: { paper: { backgroundColor: surface.base, borderColor: surface.border } } },
      MuiDialog: { styleOverrides: { paper: { backgroundColor: surface.overlay, backgroundImage: "none" } } },
      MuiPopover: { styleOverrides: { paper: { backgroundColor: surface.overlay, backgroundImage: "none" } } },
      MuiMenu: { styleOverrides: { paper: { backgroundColor: surface.overlay, backgroundImage: "none" } } },
      MuiMenuItem: { styleOverrides: { root: { color: surface.text, "&:hover": { backgroundColor: surface.hover } } } },
      MuiListItemButton: { styleOverrides: { root: { "&:hover": { backgroundColor: surface.hover } } } },
      MuiDivider: { styleOverrides: { root: { borderColor: surface.border } } },
      MuiTooltip: {
        styleOverrides: {
          tooltip: { backgroundColor: surface.raised, color: surface.text, border: `1px solid ${surface.border}` },
          arrow: { color: surface.raised },
        },
      },
      MuiTableCell: { styleOverrides: { root: { borderColor: surface.border, color: surface.text } } },
      MuiTableHead: { styleOverrides: { root: { backgroundColor: surface.sunken } } },
      MuiOutlinedInput: {
        styleOverrides: {
          root: { backgroundColor: surface.base },
          notchedOutline: { borderColor: surface.border },
        },
      },
      MuiFilledInput: { styleOverrides: { root: { backgroundColor: surface.sunken } } },
      MuiInputBase: { styleOverrides: { input: { "&::placeholder": { color: surface.textTertiary, opacity: 1 } } } },
      MuiChip: { styleOverrides: { outlined: { borderColor: surface.border } } },
      MuiBackdrop: { styleOverrides: { root: { backgroundColor: surface.scrim } } },
      MuiSkeleton: { styleOverrides: { root: { backgroundColor: surface.muted } } },
      MuiAccordion: { styleOverrides: { root: { backgroundColor: surface.base, backgroundImage: "none" } } },
      MuiAutocomplete: { styleOverrides: { paper: { backgroundColor: surface.overlay } } },
      MuiSnackbarContent: { styleOverrides: { root: { backgroundColor: surface.raised, color: surface.text } } },
      MuiDataGrid: {
        styleOverrides: {
          root: {
            backgroundColor: surface.base,
            color: surface.text,
            borderColor: surface.border,
            "--DataGrid-containerBackground": surface.sunken,
          },
          columnHeaders: { backgroundColor: surface.sunken, borderColor: surface.border },
          cell: { borderColor: surface.border },
          footerContainer: { borderColor: surface.border },
          row: { "&:hover": { backgroundColor: surface.hover } },
        },
      } as never,
    },
  };

  return createTheme(options);
};
